import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import EventCard from "@/components/EventCard";
import { useLang } from "@/context/LanguageContext";
import { CATEGORY_META } from "@/lib/categories";
import { Tent } from "lucide-react";

const FESTIVAL_CATEGORIES = ["pagan_festival", "market", "pagan_event", "reenactment"];

export default function Festivals() {
  const { t, tCategory } = useLang();
  const [cat, setCat] = useState("all");
  const { data: events = [] } = useQuery({
    queryKey: ["festivals"],
    queryFn: async () => (await api.get("/events", { params: { date_from: new Date().toISOString(), limit: 200 } })).data,
  });

  const festivals = useMemo(
    () => events.filter((e) => FESTIVAL_CATEGORIES.includes(e.category) && (cat === "all" || e.category === cat)),
    [events, cat]
  );

  const chip = (active) => `px-4 py-2 rounded-full text-sm border transition-all ${active ? "bg-amber-600 text-stone-950 font-semibold border-amber-400" : "bg-[#121418] text-stone-300 border-[#272A34] hover:border-amber-500/40"}`;

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const jsonLd = {
    "@context": "https://schema.org", "@type": "ItemList", name: t("festivals_title"),
    itemListElement: festivals.slice(0, 100).map((e, i) => ({
      "@type": "ListItem", position: i + 1, name: e.title, url: `${origin}/events/${e.slug}`,
    })),
  };

  return (
    <>
      <Seo title={t("festivals_title")} description={t("festivals_desc")} canonicalPath="/festivals" jsonLd={jsonLd} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
        <div className="flex items-center gap-3 mb-3"><Tent className="w-7 h-7 text-amber-500" /><h1 className="font-serif text-3xl sm:text-4xl uppercase tracking-tight text-amber-100">{t("festivals_title")}</h1></div>
        <p className="text-stone-400 mb-8 max-w-2xl">{t("festivals_desc")}</p>

        <div className="flex flex-wrap gap-2 mb-10" data-testid="festivals-filters">
          <button data-testid="festivals-cat-all" onClick={() => setCat("all")} className={chip(cat === "all")}>{t("filter_all")}</button>
          {FESTIVAL_CATEGORIES.map((c) => <button key={c} data-testid={`festivals-cat-${c}`} onClick={() => setCat(c)} className={chip(cat === c)}>{tCategory(c)}</button>)}
        </div>

        {festivals.length === 0 ? (
          <div className="text-center py-16 text-stone-400 border border-dashed border-[#272A34] rounded-lg" data-testid="festivals-empty">{t("festivals_empty")}</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="festivals-grid">
            {festivals.map((e, i) => <EventCard key={e.slug} event={e} index={i} />)}
          </div>
        )}
      </div>
    </>
  );
}
